import { useEffect, useState } from 'react';
import * as React from 'react';
import { useSelector } from 'react-redux';
import AddBoxRoundedIcon from '@mui/icons-material/AddBoxRounded';
import { styled } from '@mui/material/styles';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem, treeItemClasses } from '@mui/x-tree-view/TreeItem';
import { getModules, createModule, updateModule, deleteModule } from '../../utils/api/module_apis';
import { STATUS } from '../../utils/api/sharedfunction';
import {  
  TextField, 
  IconButton,
  Box,
  Tooltip,
  Typography,
  Divider,
  CircularProgress
} from '@mui/material';
import { 
  FolderOpenTwoTone,
  FolderCopyOutlined as AllModuleIcon,
  FolderOutlined as ModuleIcon,
  CreateNewFolder as CreateNewFolderIcon,
  Search as SearchIcon,
  EditOutlined as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandAllIcon,
  UnfoldLess as CollapseAllIcon,
  UnfoldMoreOutlined as ExpandAllGlobleIcon,
  UnfoldLessOutlined as CollapseAllGlobleIcon,
  CreateNewFolderOutlined as CreateModuleicon,
} from '@mui/icons-material';

import ConfirmationModal from '../Modal/ConfirmationModal';
import EditModuleModal from '../Modal/EditModuleModal';
import AddModuleModal from '../Modal/AddModuleModal';
import ActionMenu from '../menu/ActionMenu';
import GenericActionMenu from '../menu/GenericActionMenu';
import { useSnackbar } from '../../utils/useSnackbar';
import { getAddButtonText, getEditModalTitle, getTerminologyForLevel, getDeleteCascadeText, canHaveChildren } from '../../utils/moduleTerminology';
import { getDeleteModalTitle, getDeleteConfirmMessage } from '../../utils/uiText';
import theme from '../../theme/theme';

// Enhanced TreeItem with proper styling
const CustomTreeItem = styled(TreeItem)(({ theme }) => ({
  [`& .${treeItemClasses.content}`]: {
    padding: theme.spacing(1, 1.5),
    margin: theme.spacing(0, 0),
    borderRadius: '0',

    // Hover
  '&:hover': {
    backgroundColor: 'rgba(246, 246, 246, 1)',
  },

  // Focus
  '&:focus': {
    backgroundColor: 'rgba(230, 231, 234, 1)',
  },

  // Selected + Focused
  '&.Mui-selected.Mui-focused': {
    backgroundColor: 'rgba(230, 231, 234, 1)',
  },

  // ✅ Selected + NOT Focused (your missing case)
  '&.Mui-selected:not(.Mui-focused)': {
    backgroundColor: 'rgba(230, 231, 234, 1)',
  },
  },
  [`& .${treeItemClasses.iconContainer}`]: {
    margin: theme.spacing(0, 0.3),
    '& .close': {
      opacity: 0.3,
    },
    '& svg': {
      fontSize: '1.4rem !important',
      opacity: 0.6,
    },
  },
  [`& .${treeItemClasses.groupTransition}`]: {
    marginLeft: 5,
    paddingLeft: 12,
  },
}));

// Custom module name component with tooltip for long text
const ModuleName = ({ name, fontSize }) => {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = React.useRef(null);

  React.useEffect(() => {
    if (textRef.current) {
      setIsOverflowing(textRef.current.scrollWidth > textRef.current.clientWidth);
    }
  }, [name]);

  return (
    <Tooltip title={isOverflowing ? name : ''} arrow placement="top">
      <span
        ref={textRef}
        style={{
          fontSize,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: '200px',
          display: 'block'
        }}
      >
        {name}
      </span>
    </Tooltip>
  );
};

const ModuleTreeView = ({ 
  onLeafNodeClick, 
  onModulesUpdate,  
  disableActions = false, 
  showAllTest = false, 
  showHeader = false
}) => {
  const shouldRenderHeader = showHeader || !disableActions;
  const defaultModuleName = "All Test Cases";
  const [modules, setModules] = useState([]);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredModules, setFilteredModules] = useState([]); 

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newNode, setNewNode] = useState({ name: '', parentId: null });
  const [parentNode, setParentNode] = useState(null);

  const [openEditModal, setOpenEditModal] = useState(false);
  const [editNode, setEditNode] = useState(null);
 
  const [deleteId, setDeleteId] = useState(null);
  const [nodeToDelete, setNodeToDelete] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [hoveredNodeId, setHoveredNodeId] = useState(null);

  const [isRootAddModalOpen, setIsRootAddModalOpen] = useState(false);
  const selectedProject = useSelector(state => state.projects.selectedProject);

  const { showSnackbar } = useSnackbar();
  const [addModalTitle, setAddModalTitle] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [expanded, setExpanded] = useState([]);

  // Handle expansion state
  const handleNodeToggle = (event, nodeIds) => {
    setExpanded(nodeIds);
  };

  // Expand all nodes
  const expandAll = () => {
    const getAllIds = (nodes) => {
      let ids = [];
      nodes.forEach(node => {
        ids.push(String(node.id));
        if (node.children?.length) {
          ids = [...ids, ...getAllIds(node.children)];
        }
      });
      return ids;
    };
    
    const allIds = getAllIds(modules);
    setExpanded(allIds);
  };

  // Collapse all nodes
  const collapseAll = () => {
    setExpanded([]);
  };

  useEffect(() => {
    const fetchModules = async () => {
      if (!selectedProject?.id) return;
      
      setIsLoading(true);
      try {
        const result = await getModules();
        
        if (result.status === STATUS.SUCCESS && result.data) {
          const fetchedModules = result.data;
          setModules(fetchedModules);
          setFilteredModules(fetchedModules);
          
          // Auto-expand first level nodes for better UX
          // const firstLevelIds = fetchedModules
          //   .filter(module => module.level === 1)
          //   .map(module => String(module.id));
          // setExpanded(firstLevelIds);
        } else {
          showSnackbar(result.error || "Failed to fetch modules", "error");
        }
      } catch (error) {
        console.error('Error fetching modules:', error);
        showSnackbar("Failed to fetch modules", "error");
      } finally {
        setIsLoading(false);
      }
    };

    fetchModules();
  }, [selectedProject?.id]);

  useEffect(() => {
    if (!disableActions) {
      onLeafNodeClick("default", defaultModuleName)
    }
  }, [modules]);

  const filterTree = (nodes, query) => {
    if (!query.trim()) return nodes;
    
    return nodes
      .map((node) => {
        if (node.name.toLowerCase().includes(query.toLowerCase())) {
          return node;
        }
        if (node.children?.length) {
          const filteredChildren = filterTree(node.children, query);
          if (filteredChildren.length > 0) {
            return { ...node, children: filteredChildren };
          }
        }
        return null;
      })
      .filter(Boolean);
  };

  const handleSearch = (event) => {
    const query = event.target.value;
    setSearchQuery(query);

    if (query.trim() === '') {
      setFilteredModules(modules);
    } else {
      const filtered = filterTree(modules, query);
      setFilteredModules(filtered);
      
      // Expand all parent nodes of matched results for better visibility
      if (filtered.length) {
        const allNodeIds = getAllNodeIds(filtered);
        setExpanded(allNodeIds);
      }
    }
  };

  // Helper to get all node IDs for expansion during search
  const getAllNodeIds = (nodes) => {
    let ids = [];
    nodes.forEach(node => {
      ids.push(String(node.id));
      if (node.children?.length) {
        ids = [...ids, ...getAllNodeIds(node.children)];
      }
    });
    return ids;
  };

  const openAddModal = (parentId) => {
    setParentNode(parentId);
    const parentNodeData = findNodeById(modules, parentId);
    if (!parentNodeData) return;
    
    const modalTitle = parentNodeData.level === 2 ? "Add Module" : "Add Module";
    setAddModalTitle(modalTitle);
    setIsAddModalOpen(true);
  };

  const closeAddModal = () => {
    setIsAddModalOpen(false);
    setParentNode(null);
  };

  const findNodeById = (nodes, id) => {
    for (const node of nodes) {
        if (node.id === id) {
            return node;
        }
        if (node.children?.length) {
            const found = findNodeById(node.children, id);
            if (found) return found;
        }
    }
    return null;
  };

  const openRootAddModal = () => {
    setIsRootAddModalOpen(true);
  };

  const closeRootAddModal = () => {
    setIsRootAddModalOpen(false);
  };

  const confirmAddRootNode = async (nodeName, context) => {
    // Validate input
    if (!nodeName?.trim()) {
      showSnackbar("Please provide a valid module name", "error");
      return;
    }

    const isDuplicate = modules.some(
      (module) => module.name.toLowerCase() === nodeName.toLowerCase() && module.level === 1
    );
    
    if (isDuplicate || (nodeName.toLowerCase() === defaultModuleName.toLowerCase())) {
      showSnackbar("A module with the same name already exists", "error");
      return;
    }

    try {
      const result = await createModule({
        name: nodeName,
        parentId: null,
        context: context,
        level: 1,
      });

      if (result.status === STATUS.SUCCESS && result.data?.id) {
        const newModule = {
          id: result.data.id,
          name: nodeName,
          level: 1,
          context: context,
          children: [],
        };

        const updatedModules = [...modules, newModule];
        setModules(updatedModules);
        setFilteredModules(updatedModules);
        
        if (onModulesUpdate) {
          onModulesUpdate(updatedModules);
        }

        closeRootAddModal();
        showSnackbar(result.message || "Module added successfully", "success");
      } else {
        showSnackbar(result.error || "Failed to add module", "error");
      }
    } catch (error) {
      console.error('Error adding new root module:', error);
      showSnackbar("Failed to add module", "error");
    }
  };

  const confirmAddNode = async (nodeName, context) => {
    // Validate input
    if (!nodeName?.trim() || !parentNode) {
      showSnackbar("Please provide a valid module name", "error");
      return;
    }

    try {
      const parentNodeData = findNodeById(modules, parentNode);
      if (!parentNodeData) {
        console.error('Parent node not found for ID:', parentNode);
        showSnackbar("Parent module not found", "error");
        return;
      }

      // Check for duplicate names among siblings
      const siblings = parentNodeData.children || [];
      const isDuplicate = siblings.some(
        child => child.name.toLowerCase() === nodeName.toLowerCase()
      );
      
      if (isDuplicate) {
        showSnackbar("A module with the same name already exists", "error");
        return;
      }

      const level = parentNodeData.level + 1;
      const result = await createModule({
        name: nodeName,
        parentId: parentNode,
        context: context,
        level,
      });

      if (result.status === STATUS.SUCCESS && result.data?.id) {
        // Update the tree structure
        const addChildToNode = (nodes) => nodes.map((node) => {
          if (node.id === parentNode) {
            const newChild = { 
              id: result.data.id, 
              name: nodeName, 
              level, 
              context,
              children: [] 
            };
            return { 
              ...node, 
              children: [...(node.children || []), newChild] 
            };
          }
          if (node.children?.length) {
            return { ...node, children: addChildToNode(node.children) };
          }
          return node;
        });

        const updatedModules = addChildToNode(modules);
        setModules(updatedModules);
        setFilteredModules(addChildToNode(filteredModules));
        
        if (onModulesUpdate) {
          onModulesUpdate(updatedModules);
        }
        
        // Auto-expand the parent to show the new child
        if (!expanded.includes(String(parentNode))) {
          setExpanded([...expanded, String(parentNode)]);
        }
        
        closeAddModal();
        showSnackbar(result.message || "Module added successfully", "success");
      } else {
        showSnackbar(result.error || "Failed to add module", "error");
      }
    } catch (error) {
      console.error('Error adding new module:', error);
      showSnackbar("Failed to add module", "error");
    }
  };

  const confirmEditNode = async (newName, context) => {
    // Validate input
    if (!newName?.trim() || !editNode?.id) {
      showSnackbar("Please provide a valid module name", "error");
      return;
    }

    try {
      // Find parent to check for duplicate names among siblings
      let parent = null;
      const findParent = (nodes, nodeId) => {
        for (const node of nodes) {
          if (node.children?.some(child => child.id === nodeId)) {
            return node;
          }
          if (node.children?.length) {
            const found = findParent(node.children, nodeId);
            if (found) return found;
          }
        }
        return null;
      };

      // Only root nodes don't have parents
      if (editNode.level > 1) {
        parent = findParent(modules, editNode.id);
        
        // Check for duplicate names among siblings
        if (parent?.children) {
          const isDuplicate = parent.children
            .filter(child => child.id !== editNode.id) // Exclude current node
            .some(child => child.name.toLowerCase() === newName.toLowerCase());
            
          if (isDuplicate) {
            showSnackbar("A module with the same name already exists", "error");
            return;
          }
        }
      } else {
        // Check for duplicates at root level
        const isDuplicate = modules
          .filter(module => module.id !== editNode.id) // Exclude current node
          .some(module => module.level === 1 && module.name.toLowerCase() === newName.toLowerCase());
          
        if (isDuplicate || newName.toLowerCase() === defaultModuleName.toLowerCase()) {
          showSnackbar("A module with the same name already exists", "error");
          return;
        }
      }

      // Update module on the server
      const result = await updateModule({
        id: editNode.id,
        name: newName,
        context: context,
      });

      if (result.status === STATUS.SUCCESS) {
        // Update module in the state
        const updateNodeInTree = (nodes) => nodes.map((node) => {
          if (node.id === editNode.id) {
            return { ...node, name: newName, context: context };
          }
          if (node.children?.length) {
            return { ...node, children: updateNodeInTree(node.children) };
          }
          return node;
        });

        const updatedModules = updateNodeInTree(modules);
        setModules(updatedModules);
        setFilteredModules(updateNodeInTree(filteredModules));
        
        if (onModulesUpdate) {
          onModulesUpdate(updatedModules);
        }
        
        setOpenEditModal(false);
        showSnackbar(result.message || "Module updated successfully", "success");
      } else {
        showSnackbar(result.error || "Failed to update module", "error");
      }
    } catch (error) {
      console.error('Error updating node:', error);
      showSnackbar("Failed to update module", "error");
    }
  };

  const confirmDeleteNode = async () => {
    if (!deleteId) return;
    
    try {
      const result = await deleteModule({ id: deleteId });
      
      if (result.status === STATUS.SUCCESS) {
        // Remove the node from the tree
        const removeNodeFromTree = (nodes) => {
          return nodes.filter(node => node.id !== deleteId)
            .map(node => {
              if (node.children?.length) {
                return { ...node, children: removeNodeFromTree(node.children) };
              }
              return node;
            });
        };
        
        const updatedModules = removeNodeFromTree(modules);
        setModules(updatedModules);
        setFilteredModules(removeNodeFromTree(filteredModules));
        
        if (onModulesUpdate) {
          onModulesUpdate(updatedModules);
        }
        
        showSnackbar(result.message || "Module deleted successfully", "success");
      } else {
        showSnackbar(result.error || "Failed to delete module", "error");
      }
    } catch (error) {
      console.error('Error deleting module:', error);
      showSnackbar("Failed to delete module", "error");
    } finally {
      setDeleteId(null);
      setNodeToDelete(null);
      setOpenModal(false);
    }
  };

  const openDeleteModal = (nodeId) => {
    const node = findNodeById(modules, nodeId);
    setDeleteId(nodeId);
    setNodeToDelete(node);
    setOpenModal(true);
  };

  const cancelDelete = () => {
    setDeleteId(null);
    setNodeToDelete(null);
    setOpenModal(false);
  };

  // Helper to check if a node has children
  const hasChildren = (node) => {
    return node.children && node.children.length > 0;
  };

  const renderTree = (nodes) => {
    return nodes.map((node) => {
      if (!node?.id) {
        console.error("Node is missing an id:", node);
        return null;
      }
      
      const isLeafNode = !hasChildren(node);
      const fontSize = node.level === 1 ? '16px' : '15px';
      
      // Generate action buttons based on node properties
      const addButtonText = getAddButtonText(node.level);
      const rowActionButtons = [
        ...(canHaveChildren(node.level) ? [{
          name: addButtonText.name,
          tooltip: addButtonText.name,
          icon: <AddBoxRoundedIcon />,
          action: (row) => openAddModal(row.id),
          disabled: false,
        }] : []),
        {
          name: 'Edit',
          tooltip: 'Edit',
          icon: <EditIcon />,
          action: (row) => {
            setEditNode(row);
            setOpenEditModal(true);
          },
          disabled: false,
        },
        {
          name: 'Delete',
          tooltip: 'Delete',
          icon: <DeleteIcon />,
          action: (row) => openDeleteModal(row.id),
          disabled: false,
        },
      ];
      
      return (
        <CustomTreeItem 
          key={node.id} 
          itemId={String(node.id)}
          label={
            <div
              onMouseEnter={() => setHoveredNodeId(node.id)} 
              onMouseLeave={() => setHoveredNodeId(null)}
              style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                width: '100%'
              }}
              onClick={(e) => {
                //console.log('Node clicked:', node);
                // Only trigger the click handler if it's not coming from action buttons
                if (e.target.closest('.module-action-menu') === null) {
                  onLeafNodeClick(node.id, node.name);
                }
              }}
            >
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: 1,
                  overflow: 'hidden',
                  flex: 1
                }}
              >
                <ModuleIcon 
                  sx={{ 
                    opacity: isLeafNode ? 0.5 : 1, 
                    color: '#1B0CA6',
                  }} 
                />
                <ModuleName name={node.name} fontSize={fontSize} />
              </Box>
              
              {/* Conditional rendering for action buttons */}
              {!disableActions && (
                <div 
                  className="module-action-menu"
                  style={{ 
                    visibility: hoveredNodeId === node.id ? 'visible' : 'hidden',
                    marginLeft: 'auto'
                  }}
                >
                  <ActionMenu 
                    menuItems={rowActionButtons}
                    rowData={node}
                  />
                </div>
              )}
            </div>
          }
        >
          {hasChildren(node) && renderTree(node.children)}
        </CustomTreeItem>
      );
    });
  };

    // Row action buttons
    const headerActionButtons = [
      {
        name: "Add Module",
        tooltip: "Add New Module",
        icon: <CreateModuleicon />,
        action: () => openRootAddModal(),
        disabled: false,
      },
      {
        name: "Expand All",
        tooltip: "Expand All Modules",
        icon: <ExpandAllGlobleIcon />,
        action: () => expandAll(),
        disabled: modules.length === 0,
      },
      {
        name: "Collapse All",
        tooltip: "Collapse All Modules",
        icon: <CollapseAllGlobleIcon />,
        action: () => collapseAll(),
        disabled: modules.length === 0,
      },
    ];
  
  return (
    <>
      <div style={{ 
        height: '100%', 
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '12px',
        borderWidth: '2px',
        borderStyle: 'solid',
        borderColor: theme.palette.background.light,
        overflow: 'hidden',
      }}>
        {/* Header */}
        {shouldRenderHeader && (
          <>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              height: '50px',
              padding: '8px',
            }}>
              
              {/* Header - left */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="h6"
                  sx={{
                    textAlign: 'left',
                    marginLeft: '8px',
                    fontSize: '16px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Tooltip title={ "Project: " + selectedProject?.name || "<Project Name>"} arrow>
                    {selectedProject?.name || "Project Module"}
                  </Tooltip>
                </Typography>
              </div>

              {/* Header - Right */}
              {!disableActions && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginRight: '10px' }}>
                  <GenericActionMenu
                    menuItems={headerActionButtons}
                  />
                </div>
              )}
            </div>
          </>
        )}
  
      
        {/* Tree View Container with Search Bar inside */}
        <div style={{ 
          flexGrow: 1, 
          overflow: 'hidden', 
          backgroundColor: 'white',
          display: 'flex',
          flexDirection: 'column'
        }}>

        {disableActions && (
          <>
            {/* Search bar in its original location */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              padding: '10px',
            }}>
              <TextField
                label="Modules"
                value={searchQuery}
                onChange={handleSearch}
                variant="outlined"
                placeholder="Search Modules"
                fullWidth
                type='search'
                InputProps={{
                  startAdornment: (
                    <IconButton edge="end" size="small">
                      <SearchIcon fontSize="small" />
                    </IconButton>
                  ),
                }}

              />
            </div>
          </>
        )}

          
          {/* Tree View */}
          <div style={{ flexGrow: 1, overflow: 'auto' }}>
            {isLoading ? (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%' 
              }}>
                <CircularProgress />
              </Box>
            ) : (
              <SimpleTreeView
                aria-label="modules-tree"
                sx={{
                  overflowX: 'hidden',
                  overflowY: 'auto',
                  height: '100%',
                }}
                expandedItems={expanded}
                onExpandedItemsChange={handleNodeToggle}
              >
                {(showAllTest || !disableActions) && (
                  <>
                  <Divider/>
                  <CustomTreeItem
                    itemId="default"
                    label={
                      <div
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          cursor: 'pointer',
                          margin: '2px 0px',
                        }}
                        onClick={() => onLeafNodeClick("default", defaultModuleName)}
                      >
                        <Box sx={{ 
                          mr: 1,
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                          <AllModuleIcon 
                            sx={{ 
                              color: '#1B0CA6',
                            }} 
                          />
                        </Box>
                        <span style={{ fontSize: '1rem' }}>{defaultModuleName}</span>
                      </div>
                    }
                  />
                  <Divider/>
                  </>
                )}
                {renderTree(filteredModules)}
              </SimpleTreeView>
            )}
          </div>
        </div>

      </div>
  
      {/* Modals */}
      <ConfirmationModal
        open={openModal}
        onClose={cancelDelete}
        onConfirm={confirmDeleteNode}
        title={getDeleteModalTitle()}
        message={nodeToDelete ? getDeleteConfirmMessage(getTerminologyForLevel(nodeToDelete.level).singular.toLowerCase(), 1, false, getDeleteCascadeText(nodeToDelete.level)) : getDeleteConfirmMessage('item', 1, false)}
      />
  
      {editNode && (
        <EditModuleModal
          isOpen={openEditModal}
          onClose={() => setOpenEditModal(false)}
          onConfirm={confirmEditNode}
          nodeName={editNode.name}
          context={editNode.context}
          title={getEditModalTitle(editNode.level)}
        />
      )}
  
      {isAddModalOpen && (
        <AddModuleModal
          isOpen={isAddModalOpen}
          title={addModalTitle}
          onClose={closeAddModal}
          onConfirm={confirmAddNode}
        />
      )}

      {isRootAddModalOpen && (
        <AddModuleModal
          isOpen={isRootAddModalOpen}
          title="Add Module"
          onClose={closeRootAddModal}
          onConfirm={confirmAddRootNode}
        />
      )}
    </>
  );
};

export default ModuleTreeView;